/******************************************************************************
 *
 * Copyright (c) 2019-2022 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Kunal Pandey' <kunal.pandey@wakencode.com>, November 2024
 *
 *****************************************************************************/

import React, { useEffect, useState } from "react";
import { string } from "../../constant/string";
import { generateVerificationCodeForEmail, updateInfo, verifyEmailVerificationCode } from "../../network";
import WhiteLoader from "../../loader/WhiteLoader";
import GreenLoader from "../../loader/GreenLoader";
import { useAuth } from "../../context/AuthContext";
import { useUser } from "../../context/UserContext";
import Styles from "./EmailVerification.module.css";
import { SALoader } from "../../loader/SALoader";

/**
 * Email Verification page
 *
 * @param {String} setPage State for current page
 * @returns JSX for account activation  page
 */
const EmailVerification = ({ setPage, isInvited, jobRole }) => {
  // State for user entered verification code
  const [code, setCode] = useState("");
  // State for error
  const [apiError, setApiError] = useState("");
  // State to set token from auth context
  const { token, setToken } = useAuth("");
  //State to set user data from user context
  const { userData, setUserData } = useUser("");
  //State to set verified email
  const [verifiedEmail, setVerifiedEmail] = useState("");
  //State to set email as verified
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  //State to set white loader on button
  const [whiteLoading, setWhiteLoading] = useState(false);
  // State for resend button
  const [resend, setResend] = useState(false);
  // State for countdown
  const [countDown, setCountDown] = useState(30);
  // State for loader in verify button
  const [resendLoading, setResendLoading] = useState(false);
  // State for activation loader
  const [pageLoading, setPageLoading] = useState(true);

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  useEffect(() => {
    callApiToGenerateVerificationCode(userData?.userDetails?.email);
    setResend(true);
    resendCountDown(30);
    // Update countdown state for every second
    setTimeout(() => {
      setResend(false);
      setCountDown(30);
    }, 30000);
    // Send verification code to user
  }, []);

  /**
   * Input handle change
   *
   * @param {MouseEvent} e Input change event
   */
  const handleVerificationCodeChange = (e) => {
    let otp = e.target.value.replace(/\D/g, "").slice(0, 6);
    if (otp === "" || /^[0-9]+$/.test(otp)) {
      setCode(otp.slice(0, 6));
    }
    setApiError("");
  };

  // handle click on 'Enter' button
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleVerifyButtonChange();
    }
  };

  /**
   * Verify button handler
   *
   * @param {*} e Event for verify button click
   * @returns {Void}
   */
  const handleVerifyButtonChange = (e) => {
    if (code.length < 6) {
      setApiError(string.INVALID_VERIFICATION_CODE);
      return;
    }
    // Call api to verify user entered verification code
    callApiToVerifyEmail();
  };

  /**
   * Updating state for countdown by calling recursive function
   *
   * @param {Number} count present state of countdown
   * @returns {Void}
   */
  const resendCountDown = (count) => {
    if (count <= 0) {
      return;
    }
    count = count - 1;
    setTimeout(() => {
      setCountDown(count);
      resendCountDown(count);
    }, 1000);
  };

  /**
   * Resend button handler
   *
   * @param {*} e Event for resend button click
   */
  const handleResendButtonChange = (e) => {
    e.preventDefault();
    setResendLoading(true);
    callApiToGenerateVerificationCode(userData?.userDetails?.email);
    setCountDown(30);
    resendCountDown(30);
    setTimeout(() => {
      setResend(false);
      setCountDown(30);
    }, 30000);
  };

  /**
   * Back button handler
   *
   * @param {*} e Event for Back button click
   */
  const handleBackButtonChange = (e) => {
    e.preventDefault();
    setPage("PersonalDetails");
  };

  /**
   * Call api to send verification code to email
   */
  const callApiToGenerateVerificationCode = (email) => {
    //create request body for api
    let postData = {};
    let payload = localStorage.getItem("payload");

    if (isInvited && payload) {
      postData = {
        email: email,
        payload: payload,
      };
    } else {
      postData = {
        email: email,
      };
    }

    // Call api to get user information
    generateVerificationCodeForEmail(config, postData)
      .then((response) => {
        if (response.data?.isVerified == true) {
          setVerifiedEmail(userData?.userDetails?.email);
          setIsEmailVerified(true);
          let newUserData = { ...userData, verifiedEmail: userData?.userDetails?.email, isEmailVerified: true };
          setUserData((prev) => ({ ...prev, ...newUserData }));
          callApiToSaveData(newUserData);
        } else {
          setPageLoading(false);
        }
        setResendLoading(false);
        setResend(true);
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.errorDescription) {
          setApiError(err.response.data.errorDescription);
        }else if(err?.response?.status === 401){
          setToken("")
          setPage("SignUp")
        }
        setResendLoading(false);
        setPageLoading(false);
      });
  };

  /**
   * Call api to check email verification code
   */
  const callApiToVerifyEmail = () => {
    setWhiteLoading(true);

    //create request body for api
    let postData = {
      email: userData?.userDetails?.email,
      code: code,
    };

    // Call api to get user information
    verifyEmailVerificationCode(config, postData)
      .then((response) => {
        if (response.data?.token === null) {
          setVerifiedEmail(userData?.userDetails?.email);
          setIsEmailVerified(true);
          let newUserData = { ...userData, verifiedEmail: userData?.userDetails?.email, isEmailVerified: true };
          setUserData((prev) => ({ ...prev, ...newUserData }));
          // Call api to save verified Email
          callApiToSaveData(newUserData);
        }
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.errorDescription) {
          setApiError(err.response.data.errorDescription);
        }
        setWhiteLoading(false);
      });
  };

  /**
   * Call update api to save all the user entered information
   * @param {object} updatedUserData data to be sent as request body
   */
  const callApiToSaveData = (updatedUserData) => {
    // Prepare correct format for request body
    let postData = updatedUserData;
    // Call api to update/save user information
    updateInfo(postData, config)
      .then(() => {
        // Set current page to work details empolyee count page
        if (isInvited) {
          setPage("WorkSizeDetails");
        } else if (jobRole === "owner") {
          setPage("WorkDetails");
        } else {
          setPage("WorkSizeDetails");
        }
        setWhiteLoading(false);
        setPageLoading(false);
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          setApiError(string.SOMETHING_WENT_WRONG_TRY_AGAIN);
        } else if (err.response && err.response.data && err.response.data.errorDescription) {
          setApiError(err.response.data.errorDescription);
        }
        setWhiteLoading(false);
        setPageLoading(false);
      });
  };

  return (
    <>
      {pageLoading ? (
        <div className="adjustLoaderForFullScreen">
          <SALoader />
        </div>
      ) : (
        <div className="main">
          <div className="headText">{string.VERIFICATION_CODE}</div>
          {/* Line under head text */}
          <div className="line"></div>
          {/* Sub-heading under line */}
          <div className="text-center">
            {string.WE_HAVE_SENT_A_CODE_TO_EMAIL}
            <br />
            <br /> {userData?.userDetails?.email}
            <br />
            <br />
          </div>
          <div>
            <div className="tenREM">
              <input
                className={apiError ? "wrongInput" : "input"}
                onChange={handleVerificationCodeChange}
                placeholder={string.ENTER_VERIFICATION_CODE}
                name="emailVerificationCode"
                onKeyDown={handleKeyDown}
                value={code}
              ></input>
              {apiError ? <p className="errorText">{apiError}</p> : null}
            </div>
            <div>
              <button className="nextButton" onClick={!whiteLoading ? handleVerifyButtonChange : null}>
                {" "}
                {whiteLoading ? <WhiteLoader /> : string.VERIFY}
              </button>
            </div>
            {/* Resend code button */}
            <div>
              {resend ? (
                <button className={Styles.sentCode}>
                  {" "}
                  {string.RESEND_CODE} ({countDown})
                </button>
              ) : (
                <button className={Styles.resendCode} onClick={handleResendButtonChange}>
                  {" "}
                  {resendLoading ? <GreenLoader /> : string.RESEND_CODE}
                </button>
              )}
            </div>
            {/* Back button */}
            <div>
              <button className="backButton" onClick={handleBackButtonChange}>
                {string.BACK}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailVerification;
